import { useEffect, useState } from 'react';
import { collection as fbCollection, getDoc, doc as fbDoc, getFirestore as fbGetFirestore, query, where } from "firebase/firestore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CalendarIcon, PlusCircle, Trash2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DatePicker } from "../../components/ui/date-picker";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Textarea } from "../../components/ui/textarea";
import Header from '../../components/layout/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useToast } from "../../hooks/use-toast";
import { Switch } from "../../components/ui/switch";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { app } from "../../firebase";
import { getAuth } from "firebase/auth";
import { Navigate, useNavigate } from 'react-router-dom';

// Form validation schema - similar to StudentRegistration but without fee details
const draftStudentFormSchema = z.object({
  // Personal Info  
  studentsName: z.string().min(3, { message: "Name must be at least 3 characters" }),
  fathersName: z.string().min(3, { message: "Father's name is required" }),
  mothersName: z.string().min(3, { message: "Mother's name is required" }),
  graduation: z.string().min(2, { message: "Graduation is required" }),
  stream: z.string().min(2, { message: "Branch/Stream is required" }),
  yearOfPassedOut: z.string().min(4, { message: "Year passed is required" }),
  dateOfBirth: z.date({ required_error: "Date of birth is required" }),
  aadharNumber: z.string().min(12, { message: "Valid Aadhar number is required" }),
  gender: z.enum(["Male", "Female"], { required_error: "Gender is required" }),
  nationality: z.string().min(2, { message: "nationality is required" }),
  professionalType: z.enum(["Fresher", "Experienced"], { required_error: "Type is required" }),


  // Present Address  
  presentAddress: z.object({
    fullAddress: z.string().min(10, { message: "Full address is required" }),
    district: z.string().min(2, { message: "District is required" }),
    state: z.string().min(2, { message: "State is required" }),
    pincode: z.string().min(6, { message: "Valid pincode is required" }),
  }),

  // Permanent Address  
  permanentAddress: z.object({
    fullAddress: z.string().min(10, { message: "Full address is required" }),
    district: z.string().min(2, { message: "District is required" }),
    state: z.string().min(2, { message: "State is required" }),
    pincode: z.string().min(6, { message: "Valid pincode is required" }),
  }),

  // Course Info - Updated to include isOnline flag
  courseInfo: z.object({
    courses: z.array(
      z.object({
        courseId: z.string(),
        courseName: z.string(),
        isOnline: z.boolean().default(false),
      })
    ).min(1, { message: "At least one course is required" }),
  }),

  // Additional Info  
  emailId: z.string().email({ message: "Valid email is required" }),
  mobileNumber: z.string().min(9, { message: "Valid phone number is required" }),
  maritalStatus: z.enum(["single", "married"], { required_error: "Marital status is required" }),
  religion: z.string().min(0, { message: "Religion is required" }),
  bloodGroup: z.string().min(0, { message: "Blood group is required" }),
  referenceMobile: z.string().optional(),
  referencePerson: z.string().optional(),
  studentIdNumber: z.string().optional(),

  // Fee Information  
  feeInfo: z.object({
    feeTotal: z.number().optional(),
    feePaid: z.number().optional(),
    feeDue: z.number().optional(),
  }),
});

// Available courses for selection
const AVAILABLE_COURSES = [
  { id: 'course-1', name: 'Full Stack Web Development' },
  { id: 'course-2', name: 'Python Developer' },
  { id: 'course-3', name: 'Java Full Stack' },
  { id: 'course-4', name: 'React Native' },
  { id: 'course-5', name: 'UI/UX Design' },
  { id: 'course-6', name: '.NET Core Backend Development' },
  { id: 'course-7', name: 'Angular Web Development' },
  { id: 'course-8', name: 'MERN Stack Development' },
];

const DraftRegistration = ({ isLoggedIn = false }) => {
  const [userRole, setUserRole] = useState<string>("");
  const [user, setUser] = useState<any>(null);
  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const auth = getAuth(app);
        const db = fbGetFirestore(app);
        const currentUser = auth.currentUser;
        if (currentUser) {
          // Fetch user by emailId using Firestore query
          const userEmail = currentUser.email;
          if (userEmail) {
            const studentQuery = fbCollection(db, "users");
            const q = query(studentQuery, where("uid", "==", currentUser.uid));
            const studentSnapshot = await getDocs(q);
            const studentDoc = studentSnapshot.docs[0];
            if (studentDoc.data()) {
              setUser(studentDoc.data());
              setUserRole(studentDoc.data().role || "");
            }
          }
        }
      } catch (e) {
        setUserRole("");
      }
    };
    fetchUserRole();
  }, []);
  const { toast } = useToast();
  const [addressSame, setAddressSame] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<{ courseId: string; courseName: string, isOnline: boolean }[]>([]);
  const [courseToAdd, setCourseToAdd] = useState('');
  const db = getFirestore(app);
  const auth = getAuth(app);

  const [selectedTab, setSelectedTab] = useState('personal-info');

  const [courses, setFilteredCourses] = useState([]);

  const navigate = useNavigate();

  const form = useForm<z.infer<typeof draftStudentFormSchema>>({
    resolver: zodResolver(draftStudentFormSchema),
    defaultValues: {
      studentsName: '',
      fathersName: '',
      mothersName: '',
      graduation: '',
      stream: '',
      dateOfBirth: null,
      yearOfPassedOut: '',
      aadharNumber: '',
      gender: 'Male',
      professionalType: 'Experienced',
      presentAddress: {
        fullAddress: '',
        district: '',
        state: '',
        pincode: '',
      },
      permanentAddress: {
        fullAddress: '',
        district: '',
        state: '',
        pincode: '',
      },
      courseInfo: {
        courses: [],
      },
      emailId: '',
      mobileNumber: '',
      studentIdNumber: '',
      maritalStatus: 'single',
      nationality: 'Indian',
      religion: '',
      bloodGroup: '',
      referenceMobile: '',
      referencePerson: '',
      feeInfo: {
        feeDue: 0,
        feePaid: 0,
        feeTotal: 0,
      },
    },
  });

  const { setValue, getValues, formState: { isValid, isSubmitting } } = form;

  useEffect(() => {
    if (addressSame) {
      copyAddress(); // Call copyAddress when addressSame changes to true  
    }

    const coursesUnsubscribe = onSnapshot(
      collection(db, "courses"),
      (querySnapshot) => {
        //   setLoader(true); // Show loader while fetching data
        const result = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data()
          };
        });
        setFilteredCourses(result);
      },
      (error) => {
      }
    );


    return () => {
      coursesUnsubscribe();
    }
  }, [addressSame])

  const copyAddress = () => {
    if (addressSame) {
      const presentAddress = getValues('presentAddress');
      console.log('Copying present address to permanent address:', presentAddress); // Log values to copy  
      setValue('permanentAddress', {
        fullAddress: presentAddress.fullAddress,
        district: presentAddress.district,
        state: presentAddress.state,
        pincode: presentAddress.pincode,
      });
    }
  };

  const addCourse = () => {
    if (!courseToAdd) return;

    const course = courses.find(c => c.id === courseToAdd);
    if (course && !selectedCourses.some(c => c.courseId === course.id)) {
      const updatedCourses = [...selectedCourses, { courseId: course.id, courseName: course?.courseName, isOnline: false }];
      setSelectedCourses(updatedCourses);
      form.setValue('courseInfo.courses', updatedCourses);
    }

    setCourseToAdd('');
  };


  const removeCourse = (courseId: string) => {
    const updatedCourses = selectedCourses.filter(c => c.courseId !== courseId);
    setSelectedCourses(updatedCourses);
    form.setValue('courseInfo.courses', updatedCourses);
  };

  const toggleCourseMode = (courseId: string, isOnline: boolean) => {
    const updatedCourses = selectedCourses.map(course =>
      course.courseId === courseId ? { ...course, isOnline } : course
    );
    setSelectedCourses(updatedCourses);
    form.setValue('courseInfo.courses', updatedCourses);
  };

  const onSubmit = async (data: z.infer<typeof draftStudentFormSchema>) => {

    let body = {
      ...data,
      ...data['courseInfo'],
      permanentFullAddress: data?.permanentAddress['fullAddress'],
      permanentPinCode: data?.permanentAddress['pincode'],
      permanentState: data?.permanentAddress['state'],
      presentDistrict: data?.permanentAddress['district'],

      presentFullAddress: data?.presentAddress['fullAddress'],
      presentPinCode: data?.presentAddress['pincode'],
      presentState: data?.presentAddress['state'],
      permanentDistrict: data?.presentAddress['district'],
      status: 'Draft'
    };
    delete body?.courseInfo;
    delete body?.permanentAddress;
    delete body?.presentAddress;

    try {
      // Create a new reference for the user document  
      const newUserRef = doc(collection(db, "draftStudent"));

      // Combine the new document ID with the user data and set the document  
      await setDoc(newUserRef, { id: newUserRef.id, ...body });

      // Display the success toast for user creation  
      toast({
        title: "User Created",
        description: "User has been successfully created.",
      });

      // Optionally reset the form if you have that functionality in place  
      form.reset();
      navigate("/");

      // Display the success toast for draft registration  
      toast({
        title: "Draft Registration Saved",
        description: "Student information has been saved as a draft.",
      });

      // Log the submitted data  
      console.log(data);
    } catch (error) {
      // Handle any errors that may occur during the document write  
      console.error("Error creating user or saving draft:", error);
      toast({
        title: "Error",
        description: "There was an error saving the information."
      });
    }
  };
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header isLoggedIn={isLoggedIn} title="Draft Registration" subtitle="Save student information as draft" />
      {(userRole !== "MASTERADMIN" && form.getValues("professionalType") !== "Experienced") ? (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-6">
          <strong>Freshers Deprecated: Also don't give false informations!!</strong> Please contact your administrator.
        </div>
      ) : null}
      <div className="flex-1 p-6 animate-fade-in">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <Tabs defaultValue={selectedTab} value={selectedTab} onValueChange={setSelectedTab} className="space-y-10 mb-6">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4 w-full">
                <TabsTrigger value="personal-info">Personal Info</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="courses">Courses</TabsTrigger>
                <TabsTrigger value="additional-info">Additional Info</TabsTrigger>
              </TabsList>

              {/* Personal Info Tab */}
              <TabsContent value="personal-info">
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="studentsName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Student's Full Name*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="fathersName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Father's Name*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="mothersName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Mother's Name*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="graduation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Graduation*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="stream"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Branch/Stream*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="yearOfPassedOut"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Year of Passed Out*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Date of Birth*</FormLabel>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select date of birth"
                              isFormControl
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="aadharNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Aadhar Number*</FormLabel>
                            <FormControl>
                              <Input {...field} type="number" maxLength={12} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="gender"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Gender*</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex space-x-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Male" id="Male" />
                                  <Label htmlFor="Male">Male</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Female" id="Female" />
                                  <Label htmlFor="Female">Female</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="professionalType"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Candidate Type*</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex space-x-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Fresher" id="Fresher" />
                                  <Label htmlFor="Fresher">Fresher</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="Experienced" id="Experienced" />
                                  <Label htmlFor="Experienced">Experienced</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Address Tab */}
              <TabsContent value="address">
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-8">
                      <div>
                        <h3 className="text-lg font-medium mb-4">Present Address</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <FormField
                            control={form.control}
                            name="presentAddress.fullAddress"
                            render={({ field }) => (
                              <FormItem className="col-span-1 md:col-span-2">
                                <FormLabel>Full Address*</FormLabel>
                                <FormControl>
                                  <Textarea {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="presentAddress.district"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>District*</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="presentAddress.state"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>State*</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="presentAddress.pincode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Pincode*</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium">Permanent Address</h3>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="sameAsPresent"
                              checked={addressSame}
                              onChange={(e) => {
                                setAddressSame(e.target.checked);
                                if (e.target.checked) {
                                  copyAddress();
                                }
                              }}
                              className="rounded border-gray-300 text-bytesplash-blue focus:ring-bytesplash-blue"
                            />
                            <Label htmlFor="sameAsPresent">Same as Present Address</Label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <FormField
                            control={form.control}
                            name="permanentAddress.fullAddress"
                            render={({ field }) => (
                              <FormItem className="col-span-1 md:col-span-2">
                                <FormLabel>Full Address*</FormLabel>
                                <FormControl>
                                  <Textarea {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permanentAddress.district"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>District*</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permanentAddress.state"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>State*</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="permanentAddress.pincode"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Pincode*</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={addressSame} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Courses Tab */}
              <TabsContent value="courses">
                <Card>
                  <CardContent className="p-4 sm:pt-6">
                    <h3 className="text-lg font-medium mb-4">Courses</h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                        <div className="flex-1">
                          <Label htmlFor="courseSelect" className="block mb-2">Select Course*</Label>
                          <Select value={courseToAdd} onValueChange={setCourseToAdd}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select a course" />
                            </SelectTrigger>
                            <SelectContent>
                              {courses.map((course, i) => (
                                <SelectItem key={Date.now().toString() + i + 1} value={course.id}>
                                  {course.courseName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex">
                          <Button type="button" onClick={addCourse} className="w-full sm:w-auto">
                            <PlusCircle className="mr-2 h-4 w-4" />
                            Add Course
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4">
                        {selectedCourses.length > 0 ? (
                          <div className="border rounded-md overflow-hidden">
                            <div className="hidden sm:block">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Course Name
                                    </th>
                                    <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Mode
                                    </th>
                                    <th scope="col" className="px-4 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {selectedCourses.map(course => (
                                    <tr key={course.courseId}>
                                      <td className="px-4 sm:px-6 py-3 text-sm font-medium text-gray-900">
                                        {course.courseName}
                                      </td>
                                      <td className="px-4 sm:px-6 py-3 text-sm text-gray-500">
                                        <div className="flex items-center space-x-2">
                                          <Switch
                                            checked={course.isOnline}
                                            onCheckedChange={(checked) => toggleCourseMode(course.courseId, checked)}
                                            id={`course-mode-${course.courseId}`}
                                          />
                                          <Label htmlFor={`course-mode-${course.courseId}`}>
                                            {course.isOnline ? 'Online' : 'Offline'}
                                          </Label>
                                        </div>
                                      </td>
                                      <td className="px-4 sm:px-6 py-3 text-sm text-center">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-600 hover:text-red-900"
                                          onClick={() => removeCourse(course.courseId)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Mobile View */}
                            <div className="sm:hidden divide-y divide-gray-200">
                              {selectedCourses.map(course => (
                                <div key={course.courseId} className="p-4 space-y-3">
                                  <div className="flex justify-between items-start">
                                    <div className="font-medium text-gray-900">{course.courseName}</div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-900"
                                      onClick={() => removeCourse(course.courseId)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      checked={course.isOnline}
                                      onCheckedChange={(checked) => toggleCourseMode(course.courseId, checked)}
                                      id={`course-mode-mobile-${course.courseId}`}
                                    />
                                    <Label htmlFor={`course-mode-mobile-${course.courseId}`}>
                                      {course.isOnline ? 'Online' : 'Offline'}
                                    </Label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-6 sm:py-8 border rounded-md bg-gray-50">
                            <p className="text-gray-500">No courses selected</p>
                            <p className="text-sm text-gray-400">Please select at least one course</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Additional Info Tab */}
              <TabsContent value="additional-info">
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-medium mb-4">Additional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="emailId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email ID*</FormLabel>
                            <FormControl>
                              <Input {...field} type="emailId" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="mobileNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="nationality"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nationality*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="religion"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Religion*</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bloodGroup"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Blood Group*</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select blood group" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="A+">A+</SelectItem>
                                <SelectItem value="A-">A-</SelectItem>
                                <SelectItem value="B+">B+</SelectItem>
                                <SelectItem value="B-">B-</SelectItem>
                                <SelectItem value="AB+">AB+</SelectItem>
                                <SelectItem value="AB-">AB-</SelectItem>
                                <SelectItem value="O+">O+</SelectItem>
                                <SelectItem value="O-">O-</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maritalStatus"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel>Marital Status*</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="flex space-x-4"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="single" id="single" />
                                  <Label htmlFor="single">Single</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="married" id="married" />
                                  <Label htmlFor="married">Married</Label>
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="referenceMobile"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reference Mobile</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="referencePerson"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Reference Person Name</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

            </Tabs>

            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" disabled={userRole !== "MASTERADMIN" && user}>Cancel</Button>
              <Button type="submit" disabled={(userRole !== "MASTERADMIN" || !isValid || isSubmitting) && (form.getValues("professionalType") != "Experienced")}>Register Student</Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default DraftRegistration;